import { useEffect, useState, useRef, useCallback } from "react";
import _ from "lodash";
import { useDispatch, connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { validateEmail } from "common/validator";
import { useTranslation } from "react-i18next";
import { sendLoginUrl, resetLoginState } from "./login.slice";
import { ImageWithFallback } from "./ImageWithFallback";
import {
  Zap,
  CheckCircle,
  Loader2,
  AlertCircle,
  Check,
  Mail,
} from "lucide-react";
import { brandColors } from "config/colors.config";
import { AccessNotice } from "features/auth/access-notice";

function Login({ session, errMsg, isEmailSent, isLoading, hasNoActiveMembership }) {
  const dispatch = useDispatch();
  const history = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const emailInputRef = useRef(null);

  // Load saved email from localStorage on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("crm_login_email");
    if (savedEmail) {
      setEmail(savedEmail);
      setIsEmailValid(validateEmail(savedEmail));
    }
    // Trigger animations
    setIsPageLoaded(true);
    // Auto-focus the email input
    setTimeout(() => emailInputRef.current?.focus(), 100);
  }, []);

  const handleEmailChange = useCallback((e) => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      return;
    }

    if (!validateEmail(email)) {
      return;
    }

    // Save email to localStorage for future logins
    localStorage.setItem("crm_login_email", email);

    await dispatch(sendLoginUrl({ email }));
  };

  const handleTryAgain = () => {
    dispatch(resetLoginState());
    setEmail("");
    setIsEmailValid(false);
    // Auto-focus when returning to form
    setTimeout(() => emailInputRef.current?.focus(), 100);
  };

  const openEmailApp = (provider) => {
    const urls = {
      gmail: "https://mail.google.com",
      outlook: "https://outlook.live.com",
      apple: "message://",
    };

    window.open(urls[provider], "_blank");
  };

  useEffect(() => {
    if (session) {
      const redirectUrl = _.get(window, "asist.redirect");
      window.asist = null;
      if (redirectUrl) {
        history(redirectUrl);
      } else {
        history("/app");
      }
    }
  }, [session, history]);

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left Side - Image/Brand Section */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden"
        style={{
          background: `linear-gradient(to bottom right, ${brandColors.primary.base}, ${brandColors.primary.hover})`,
        }}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRhc2hib2FyZCUyMGFuYWx5dGljc3xlbnwxfHx8fDE3NjM0MTMzMTR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Business analytics dashboard"
            className="w-full h-full object-cover"
            // Phones never show this block (hidden below lg); lazy keeps them from downloading it.
            loading="lazy"
          />
          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom right, ${brandColors.gradient.overlay.from}E6, ${brandColors.gradient.overlay.to}E6)`,
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo */}
          <div
            className={`transition-all duration-500 ${
              isPageLoaded
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-5"
            }`}
          >
            <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Feature Highlights */}
          <div
            className={`max-w-md transition-all duration-700 delay-300 ${
              isPageLoaded
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h2 className="mb-6 text-2xl font-semibold">
              {t("your_company_operating_system")}
            </h2>
            <p className="mb-8 text-white/90">
              {t("complete_solution_description")}
            </p>

            <div className="space-y-4">
              {[
                t("crm_contracts_invoices_client_management"),
                t("hr_automation_employee_requests_document_flow"),
                t("asset_tracking_fuel_management_expense_control"),
                t("task_management_team_collaboration_gamification"),
              ].map((feature, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 transition-all duration-500 ${
                    isPageLoaded
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 -translate-x-8"
                  }`}
                  style={{ transitionDelay: `${500 + index * 150}ms` }}
                >
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-white/90">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div
        className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8"
        style={{
          backgroundColor: brandColors.secondary.base,
        }}
      >
        <div className="w-full max-w-md">
          {/* Logo/Brand */}
          <div
            className={`text-center mb-8 transition-all duration-500 ${
              isPageLoaded
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-5"
            }`}
          >
            <div
              className={`w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-4 transition-all duration-500 delay-200 ${
                isPageLoaded ? "scale-100" : "scale-0"
              }`}
              style={{ backgroundColor: brandColors.primary.base }}
            >
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h1 className="mb-2 text-2xl font-semibold text-gray-900">
              {t("sign_in_to_your_account")}
            </h1>
            <p className="text-gray-600">
              {t("enter_email_receive_secure_login_link")}
            </p>
          </div>

          {hasNoActiveMembership && (
            <div className="mb-6">
              <AccessNotice>{t("no_access_to_any_company")}</AccessNotice>
            </div>
          )}

          {/* Card */}
          <div
            className="border shadow-sm rounded-md p-6 mb-6"
            style={{
              backgroundColor: brandColors.form.background,
              borderColor: brandColors.form.border,
            }}
          >
            {!isEmailSent ? (
              <div
                key="login-form"
                className={`transition-opacity duration-300 ${
                  isEmailSent ? "opacity-0" : "opacity-100"
                }`}
              >
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      {t("email_address")}
                    </label>
                    <div className="relative">
                      <input
                        ref={emailInputRef}
                        id="email"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="you@company.com"
                        disabled={isLoading}
                        aria-label="Email address"
                        aria-invalid={!!errMsg}
                        aria-describedby={errMsg ? "email-error" : undefined}
                        className={`w-full px-4 py-2.5 pr-10 border rounded-md text-base focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                          errMsg
                            ? "border-red-500 focus:ring-red-500"
                            : isEmailValid
                            ? "border-green-500"
                            : "border-gray-300"
                        } ${isLoading ? "bg-gray-50 cursor-not-allowed" : ""}`}
                        onFocus={(e) => {
                          if (!errMsg && !isEmailValid) {
                            e.target.style.boxShadow = `0 0 0 2px ${brandColors.primary.base}`;
                          }
                        }}
                        onBlur={(e) => {
                          if (!errMsg && !isEmailValid) {
                            e.target.style.boxShadow = "";
                          }
                        }}
                        autoComplete="email"
                      />
                      {isEmailValid && !isLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 transition-opacity duration-200">
                          <Check className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    {errMsg && (
                      <p
                        id="email-error"
                        role="alert"
                        aria-live="polite"
                        className="mt-2 text-red-600 text-sm flex items-center gap-1 transition-opacity duration-200"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {t(errMsg)}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !isEmailValid}
                    aria-label="Send magic link to email"
                    className="w-full h-[48px] text-white rounded-md transition-[background-color,transform,box-shadow] duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: brandColors.primary.base,
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor =
                          brandColors.primary.hover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor =
                          brandColors.primary.base;
                      }
                    }}
                    onMouseDown={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor =
                          brandColors.primary.active;
                      }
                    }}
                    onMouseUp={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor =
                          brandColors.primary.hover;
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t("sending")}...
                      </>
                    ) : (
                      t("send_login_link")
                    )}
                  </button>

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-gray-700">
                      <strong className="text-blue-900">
                        {t("how_it_works")}:
                      </strong>{" "}
                      {t("we_will_send_secure_link")}
                    </p>
                  </div>
                </form>
              </div>
            ) : (
              <div
                key="success-state"
                className="space-y-5 transition-opacity duration-500"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-md flex items-center justify-center mx-auto mb-4 transition-transform duration-500">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="mb-2 text-xl font-semibold text-gray-900">
                    {t("check_your_email")}
                  </h2>
                  <p className="text-gray-600 mb-1">
                    {t("we_have_sent_login_link_to")}
                  </p>
                  <p className="text-gray-900 bg-gray-100 rounded-md px-3 py-1 inline-block max-w-full break-all">
                    {email}
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                  <p className="text-sm text-gray-700">
                    {t("link_expire_warning")}
                  </p>
                </div>

                {/* Web mail in a new tab is no help on a phone, which has its own mail app. */}
                <div className="hidden md:grid md:grid-cols-3 gap-3">
                  <button
                    onClick={() => openEmailApp("gmail")}
                    aria-label="Open Gmail"
                    className="bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 text-gray-700 py-2.5 px-4 rounded-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">Gmail</span>
                  </button>
                  <button
                    onClick={() => openEmailApp("outlook")}
                    aria-label="Open Outlook"
                    className="bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 text-gray-700 py-2.5 px-4 rounded-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">Outlook</span>
                  </button>
                  <button
                    onClick={() => openEmailApp("apple")}
                    aria-label="Open Apple Mail"
                    className="bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 text-gray-700 py-2.5 px-4 rounded-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{t("mail")}</span>
                  </button>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <button
                    onClick={handleTryAgain}
                    aria-label="Use a different email address"
                    className="w-full text-gray-600 py-2 transition-colors duration-200 text-sm"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = brandColors.primary.base;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#4B5563"; // gray-600
                    }}
                  >
                    {t("use_different_email")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">
              {t("having_trouble_signing_in")}{" "}
              <a
                href="mailto:tomas@versloplaneta.lt"
                className="hover:underline transition-colors"
                style={{ color: brandColors.primary.base }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = brandColors.primary.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = brandColors.primary.base;
                }}
                aria-label="Contact support via email"
              >
                tomas@versloplaneta.lt
              </a>
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-4">
              <a
                href="#"
                className="hover:underline transition-colors max-md:py-3.5"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = brandColors.primary.base;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6B7280"; // gray-500
                }}
              >
                {t("privacy_policy")}
              </a>
              <span>•</span>
              <a
                href="#"
                className="hover:underline transition-colors max-md:py-3.5"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = brandColors.primary.base;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6B7280"; // gray-500
                }}
              >
                {t("terms_of_service")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect((state) => ({
  session: state.authReducer.session,
  hasNoActiveMembership: state.authReducer.hasNoActiveMembership,
  isLoading: state.loginPageReducer.isLoading,
  isEmailSent: state.loginPageReducer.isEmailSent,
  errMsg: state.loginPageReducer.errMsg,
}))(Login);
